import * as React from 'react'
import { useState } from 'react'
import { HotKeys } from 'react-hotkeys'

import { Box, Button } from '@factorialco/deprecated-design-system'
import _ from 'lodash'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import last from 'lodash/last'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import InlineSearch from 'modules/core/components/InlineSearch'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useLoadingFields } from 'modules/reports/contexts/loadFieldsContext'
import useCategoryNodes from 'modules/reports/hooks/useCategoryNodes'
import { groupByCategory } from 'modules/reports/lib/muncher/fieldsGrouper'
import fieldAttributes from 'modules/reports/store/collections/fields'
import { default as FieldModel } from 'modules/reports/store/models/Field'

import FilterForm from './FilterForm'
import List from './List'

type ModalProps = React.ComponentProps<typeof Modal>

const EMPTY_FIELD: FieldModel[] = []

interface Props {
  title: string
  description: string
  onClose: ModalProps['onClose']
  onSubmit: (fields: FieldModel[]) => void
  selectMultiple: boolean
  selected: FieldModel[]
  excludeIdentifiers: boolean
  excludeTypes: string[]
  isFilter: boolean
  excludePaths: string[]
  chartType?: string
  onClickItem?: (fields: FieldModel[]) => void
}

const FieldSelectorModal = ({
  selectMultiple,
  selected,
  isFilter,
  excludeTypes,
  excludeIdentifiers,
  excludePaths,
  chartType,
  onClickItem,
  onSubmit,
  title,
  description,
  onClose,
}: Props) => {
  const [loadingFields] = useLoadingFields()
  const [search, setSearch] = useState<string>('')
  const [selectedFields, setSelectedField] = useState<FieldModel[]>([])

  const [categoryNode, categoryDispatcher] = useCategoryNodes()

  const disabledTables = new Set(
    fieldAttributes.models
      .filter((x) => x.isSqlBuilder && !x.relatable)
      .map((x) => x.attributes.table)
  )

  const disabledFields = compact(
    fieldAttributes.models
      .filter((x) => x.isSqlBuilder && !x.relatable)
      .map((x) => x.attributes.path)
  )

  const fields = useUnsafeMemo(() => {
    const schema_attributes = groupByCategory(fieldAttributes.models)

    return filter(schema_attributes, (f: FieldModel) => {
      // NOTE: if the field is the Filter Group, we do not want to filter by chart type
      const excludedField =
        (excludeIdentifiers && f.isIdentifier) ||
        includes(excludeTypes, f.attributes.type) ||
        includes(excludePaths, f.attributes.path)

      if (isFilter) return !excludedField

      return !excludedField && includes(f.attributes.supported_chart_types, chartType)
    })
  }, [isFilter, chartType, excludePaths, excludeTypes, excludeIdentifiers])

  const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const handleClickItem = useUnsafeCallback(
    (field: FieldModel) => {
      let list: FieldModel[] = []
      const foundField = selectedFields.find((f: FieldModel) => f.get('path') == field.get('path'))

      if (foundField) {
        list = selectedFields.filter((f: FieldModel) => f.get('path') != field.get('path'))
      } else {
        list = [...selectedFields, field]
      }

      const newSelectedFields = selectMultiple ? list : compact([last(list)])
      setSelectedField(newSelectedFields)

      if (onClickItem) {
        onClickItem(newSelectedFields)
      }
    },
    [onClickItem, selectMultiple, selectedFields]
  )

  const handleSubmit = useUnsafeCallback(
    async (closePortal: () => void) => {
      onSubmit(selectedFields)

      if (closePortal) {
        closePortal()
      }
    },
    [onSubmit, selectedFields]
  )

  const onChoose = useUnsafeCallback(
    (event?: KeyboardEvent | undefined) => {
      if (_.isNil(event)) return

      if (event.target instanceof HTMLInputElement) {
        const newSearch = event.target.value

        const list = new FilterForm({}).applyFilters(fields, {
          selected,
          search: newSearch,
        })

        const [item, _tail] = list

        if (!_.isNil(item)) {
          handleClickItem(item)
          onSubmit(EMPTY_FIELD)
        }
      }
    },
    [fields, handleClickItem, onSubmit, selected]
  )

  const selectedFieldsArray = useUnsafeMemo(
    () => selected.map((field) => field.attributes.path),
    [selected]
  )

  const disabled = !selectedFields.length || loadingFields

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Button disabled={disabled} submit>
          {i18n.t('common.continue')}
        </Button>
      }
    >
      <BoxList>
        <Box width="full">
          {loadingFields && (
            <Box background="grey100" justifyContent="center" height="s400">
              <Loading />
            </Box>
          )}
          {!loadingFields && (
            <HotKeys
              keyMap={{
                commandEnter: 'command+enter',
              }}
              handlers={{
                commandEnter: onChoose,
              }}
            >
              <InlineSearch search={search} onSearch={onSearch} autofocus />
            </HotKeys>
          )}
          <Box
            background="grey100"
            width="full"
            height={loadingFields ? 's0' : 's360'}
            overflowY="auto"
          >
            <List
              search={search}
              fields={fields}
              selected={selectedFieldsArray}
              selectedFields={selectedFields}
              onClickItem={handleClickItem}
              categoryNodes={categoryNode}
              nodeDispatcher={categoryDispatcher}
              disabledTables={disabledTables}
              nonRelatedFields={disabledFields}
            />
          </Box>
        </Box>
      </BoxList>
    </Modal>
  )
}

export default observer(FieldSelectorModal)
